import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnChanges,
  OnInit,
  output,
  signal,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  calculateCartTotalPoints,
  CheckoutAddress,
  computeCartSubtotalUsd,
  OrderAddressPayload,
  pickLowestValue,
  ProductDetailsCart,
  SubmitPayloadId,
  SubmitPaymentPayload,
} from '@models';
import {
  AddressFormComponent,
  createAddressForm,
  DigitsGroupingPipe,
  toCheckoutAddress,
  ZeroBalanceNoticeService,
} from '@shared';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDivider } from '@angular/material/divider';
import { ConfigurationService, OrderService, UserService } from '@core';
import { CheckoutApiService } from '../../services/checkout-api.service';
import { CheckoutPaymentOptionsFormComponent } from '../checkout-payment-options-form/checkout-payment-options-form.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

function truncateToTwoDecimals(num: number): number {
  if (!Number.isFinite(num)) return num; // Handle NaN or Infinity cases
  return Number((Math.round(num * 100) / 100).toFixed(2));
}

@Component({
  selector: 'app-checkout-payment-options',
  templateUrl: './checkout-payment-options.component.html',
  styleUrls: ['./checkout-payment-options.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    FormsModule,
    ReactiveFormsModule,
    CheckoutPaymentOptionsFormComponent,
    DigitsGroupingPipe,
    MatDivider,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    AddressFormComponent,
  ],
})
export class CheckoutPaymentOptionsComponent implements OnInit, OnChanges {
  protected readonly rewardName = inject(ConfigurationService).rewardName;
  readonly productCard = input.required<ProductDetailsCart | null>();
  readonly pointsAvailable = input.required<number>();
  readonly email = input.required<string>();
  readonly selectedAddress = input<CheckoutAddress | null>(null);
  cardType = output<string>();

  readonly submitAction = output<SubmitPaymentPayload>();
  readonly pointsChange = output<SubmitPaymentPayload>();
  readonly changePayloadIdInfo = output<SubmitPayloadId>();

  readonly paymentOptionsForm = viewChild(CheckoutPaymentOptionsFormComponent);

  private userService = inject(UserService);
  protected readonly canRedeemPoints = this.userService.canRedeemPoints;
  private destroyRef = inject(DestroyRef);
  private checkoutApiService = inject(CheckoutApiService);
  private orderService = inject(OrderService);

  readonly isSubmitTouched = signal(false);
  protected readonly authorizedPayment = signal(false);
  wrongCardUsedErrorMsg = signal('');
  loading = signal(false);
  name = signal<{ firstName: string; lastName: string } | null>(null);

  // Billing address is asked only when part of the order goes to the card (US-02.1 AC04-AC06).
  readonly isBillingSameAsShipping = signal(true);
  readonly billingForm = createAddressForm();

  private cartSubtotalUsd = computed(() => computeCartSubtotalUsd(this.productCard()));

  // Check if cart has discounted prices
  private hasDiscountedPrice = computed(() => {
    const productCard = this.productCard();
    const hasDiscount =
      productCard?.subtotalDiscountedPrice !== undefined &&
      productCard?.subtotalDiscountedPrice !== null &&
      productCard?.subtotalDiscountedPrice > 0;
    return hasDiscount;
  });

  private cartSubtotalPoints = computed(() => calculateCartTotalPoints(this.productCard(), this.hasDiscountedPrice()));

  // Prices already include tax (US-02.2), so the order total is the cart subtotal.
  public cartTotalUsd = computed(() => this.cartSubtotalUsd());

  public cartTotalPoints = computed(() => this.cartSubtotalPoints());

  pointsDefaultValue = computed(() => pickLowestValue(this.cartTotalPoints(), this.pointsAvailable()));

  // All available points are applied automatically, up to the order total (RCP-292 DoD2);
  // the player cannot choose a partial amount. Mirrors the backend CheckoutPaymentCalculator.
  protected readonly appliedPoints = computed(() =>
    this.canRedeemPoints() ? Math.max(this.pointsDefaultValue(), 0) : 0,
  );

  protected readonly hasPointsApplied = computed(() => this.appliedPoints() > 0);

  // 1 point = 1 USD, and appliedPoints never exceeds the whole dollars of the cart lines.
  private usdCoveredByPoints = computed(() => this.appliedPoints());

  balanceDue = computed(() => {
    const totalUsd = this.cartTotalUsd();
    const usdCovered = this.usdCoveredByPoints();
    const balance = Math.max(0, totalUsd - usdCovered);
    return balance;
  });

  // Points cover only whole dollars of each line, so any cents left still go to the card.
  protected readonly isPaidFullyWithPoints = computed(() => this.hasPointsApplied() && this.balanceDue() <= 0);

  isDisplayCreditCartSection = computed(() => !this.isPaidFullyWithPoints());

  private isBillingFormRequired(): boolean {
    return this.isDisplayCreditCartSection() && !this.isBillingSameAsShipping();
  }

  private billingAddress(): CheckoutAddress | null {
    return this.isBillingFormRequired() ? toCheckoutAddress(this.billingForm) : this.selectedAddress();
  }

  onBillingSameAsShippingChange(isSame: boolean): void {
    this.isBillingSameAsShipping.set(isSame);
    if (!isSame) {
      // Clearing the option shows an empty billing form (US-02.1 AC06).
      this.billingForm.reset();
    }
  }

  private focusFirstInvalidBillingField(): void {
    setTimeout(() => {
      const field = document.querySelector<HTMLElement>(
        'lib-address-form.billing-address-form input.ng-invalid, lib-address-form.billing-address-form mat-select.ng-invalid',
      );
      field?.focus();
    });
  }

  willBeBilledFromCardUsd = computed(() => this.balanceDue());

  constructor() {
    inject(ZeroBalanceNoticeService).showWhilePageIsOpen();

    // Keep the checkout page (and Order review) in sync with the automatic points / card split.
    effect(() => {
      if (!this.productCard()) {
        return;
      }
      this.pointsChange.emit({
        pointsRedeem: this.appliedPoints(),
        amountUsd: truncateToTwoDecimals(this.willBeBilledFromCardUsd()),
      });
    });
  }

  ngOnInit(): void {
    this.checkoutApiService
      .getCaptureToken()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((captureContext) => {
        this.orderService.setCaptureContextToken(captureContext);
      });
  }

  // Placing the order failed: unlock the step so the player can re-enter the card or use another one.
  // Card number / CVV are Cybersource iframes, so a fresh capture context recreates them empty.
  resetPaymentAuthorization(): void {
    this.authorizedPayment.set(false);
    this.wrongCardUsedErrorMsg.set('');
    this.billingForm.enable();
    this.paymentOptionsForm()?.form.enable();
    this.paymentOptionsForm()?.expMonthControl.reset();
    this.paymentOptionsForm()?.expYearControl.reset();
    this.paymentOptionsForm()?.enableFields();
    this.paymentOptionsForm()?.focusCardNumberOnLoad();
    this.changePayloadIdInfo.emit({ payloadId: '' });
    this.checkoutApiService
      .getCaptureToken()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((captureContext) => {
        this.orderService.setCaptureContextToken(captureContext);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (('productCard' in changes || 'pointsAvailable' in changes) && this.productCard()) {
      // A changed cart or balance changes the card amount, so a previous authorization no longer applies.
      this.changePayloadIdInfo.emit({ payloadId: '' });
    }
  }

  onSubmit(): void {
    // Points cover the whole order: no card is needed.
    if (this.isPaidFullyWithPoints()) {
      this.onActionEmit();
      return;
    }
    if (this.isBillingFormRequired() && this.billingForm.invalid) {
      this.billingForm.markAllAsTouched();
      this.isSubmitTouched.set(true);
      this.focusFirstInvalidBillingField();
      return;
    }

    if (this.paymentOptionsForm()?.isFormInvalid()) {
      this.paymentOptionsForm()?.form.markAllAsTouched();
      this.paymentOptionsForm().focusOnInvalidField();

      this.isSubmitTouched.set(true);
      return;
    }

    if (this.authorizedPayment()) {
      this.onActionEmit();
      return;
    }

    if (this.isDisplayCreditCartSection()) {
      this.authorizePayment();
      return;
    }

    this.onActionEmit();
  }

  private authorizePayment(): void {
    this.loading.set(true);
    // The casino profile has no phone.
    const address = this.billingAddress();
    const body = {
      address: {
        address: address?.addressLine1 ?? '',
        country: address?.country ?? '',
        stateCode: address?.stateCode ?? '',
        city: address?.city ?? '',
        zipCode: address?.postalCode ?? '',
      },
      payer: {
        firstName: this.name()?.firstName || this.userService.firstName(),
        middleName: this.userService.middleName(),
        lastName: this.name()?.lastName || this.userService.lastName(),
        email: this.email() ?? '',
        phoneNumber: '',
      },
    };

    this.orderService
      .authorize(body)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.authorizedPayment.set(true);
          this.billingForm.disable();
          this.loading.set(false);
          this.paymentOptionsForm().form.disable();
          this.paymentOptionsForm().disableFields();
          this.wrongCardUsedErrorMsg.set('');
          this.changePayloadIdInfo.emit({ payloadId: result.payloadId });
          this.onActionEmit();
        },
        error: (error) => {
          this.loading.set(false);
          this.wrongCardUsedErrorMsg.set(error?.error?.Errors?.[0] || 'We’re unable to process your order.');
        },
      });
  }

  private onActionEmit(): void {
    // The order always needs a billing address; without a card amount it is the shipping address.
    const billingAddress = this.billingAddress();

    const payload = {
      pointsRedeem: this.appliedPoints(),
      amountUsd: truncateToTwoDecimals(this.balanceDue()),
      billingAddress: this.convertAddressToPayload(billingAddress),
      shippingAddress: this.convertAddressToPayload(this.selectedAddress()),
      email: this.email(),
    };

    this.submitAction.emit(payload);
  }

  private convertAddressToPayload(value: CheckoutAddress | null): OrderAddressPayload {
    return {
      city: value?.city ?? '',
      countryCode: value?.country ?? '',
      postalCode: value?.postalCode ?? '',
      addressLine1: value?.addressLine1 ?? '',
      addressLine2: '',
      addressLine3: '',
      stateCode: value?.stateCode ?? '',
    };
  }

  onCardTypeChange(value: string): void {
    this.cardType.emit(value);
  }

  onNameChange({ firstName, lastName }: { firstName: string; lastName: string }) {
    this.name.set({ firstName, lastName });
  }
}
